import { useCallback, useState, FC, useEffect } from 'react';
import { css } from '@emotion/react';
import { useNavigate } from '@tanstack/react-router';
import { AiReceptionistOrg } from '@wetags/ai-receptionist';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { VoiceInterfaceView, type ScheduleEventTurn } from '@frontend/ai-receptionist-ui';
import { getJTI } from '@frontend/auth-helpers';
import { getInitialParams } from '@frontend/env';
import { http } from '@frontend/fetch';
import { useTranslation } from '@frontend/i18n';
import {
  AIRSettingsChangedPayload,
  AIRTestPanelEventPayload,
  useChannelSubscription,
  type QueueEvent,
} from '@frontend/queue';
import { useAppScopeStore } from '@frontend/scope';
import { SettingsPanelContent } from '@frontend/settings-panel-engine';
import { SurfaceHeader } from '@frontend/surface-header';
import { Button, useAlert } from '@frontend/design-system';
import { useAIRVoiceSession } from '../../hooks/use-air-voice-session';
import { AIRSalesDemoTrackingIds } from '../../tracking-ids';
import { PhoneNumberSetup, PhoneNumberConfirmation } from './phone-number-setup';

dayjs.extend(utc);
dayjs.extend(timezone);

interface AIRDemoPanelProps {
  onClose?: () => void;
  onViewAppointmentRequest?: (requestId: string) => void;
}

export const AIRDemoPanel: FC<AIRDemoPanelProps> = ({ onClose, onViewAppointmentRequest }) => {
  const { t } = useTranslation('ai-receptionist');
  const navigate = useNavigate();
  const [settingsBanner, setSettingsBanner] = useState<string | null>(null);
  const [demoStarted, setDemoStarted] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [clearMessageHistory, setClearMessageHistory] = useState(true);

  const token = getJTI();
  const { selectedLocationIds } = useAppScopeStore();
  const locationId = selectedLocationIds[0];
  const currentBaseUrl = (http.baseUrl || getInitialParams().backendApi).replace(/^https?/, 'wss');
  const alert = useAlert();

  const [showScheduleTextConfirmation] = AiReceptionistOrg.useWetag(
    'air-sales-demo-text-confirmation-card-on-schedule'
  );

  const wsUrl =
    token && locationId && currentBaseUrl
      ? `${currentBaseUrl}/v1/va-reverse-proxy/ws/connect?location_id=${locationId}&token=${token}&phone_number=${phoneNumber}&clear_message_history=${clearMessageHistory}`
      : '';

  const { state, isRecording, audioLevel, silenceThreshold, handleMicButtonPress, endCall, resetState } =
    useAIRVoiceSession({
      websocketUrl: wsUrl,
      disabled: !demoStarted,
      phoneNumber,
      onError: (error) => {
        alert.error(error.message || t('Something went wrong. Please try again.'));
      },
    });

  // Subscribe to AIR settings changed event
  const eventHandler = useCallback(
    (event: QueueEvent<AIRSettingsChangedPayload | AIRTestPanelEventPayload>) => {
      if (event.payload?.action === 'settings-changed') {
        // End the call and show banner
        endCall();
        setSettingsBanner(event.payload.message);
        return true;
      }
      return false;
    },
    [endCall]
  );

  useEffect(() => {
    // Cleanup on unmount
    return () => {
      setSettingsBanner(null);
      setDemoStarted(false);
      setPhoneNumber('');
      setClearMessageHistory(true);
    };
  }, []);

  const handleViewCalendar = useCallback(
    (turn: ScheduleEventTurn) => {
      navigate({
        to: '/schedule/calendar',
        search: {
          eventId: turn.appointmentId,
          appointmentId: turn.appointmentId,
          currentDate: turn.startTime
            ? dayjs(turn.startTime)
                .tz(turn.timezone || dayjs.tz.guess())
                .format('MM/DD/YYYY')
            : undefined,
        },
      });
    },
    [navigate]
  );

  const handleStartNewConversation = useCallback(() => {
    endCall();
    resetState();
    setDemoStarted(false);
    setSettingsBanner(null);
  }, [endCall, resetState]);

  useChannelSubscription('ai-receptionist-test-panel', eventHandler);

  if (!wsUrl) {
    return null;
  }

  const handleStartDemo = (phone: string, clearHistory: boolean) => {
    setPhoneNumber(phone);
    setClearMessageHistory(clearHistory);
    setDemoStarted(true);
  };

  return (
    <div css={styles.container}>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title>{t('AI Receptionist Demo')}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <Button
              variant='secondary'
              iconName='x'
              onClick={onClose}
              trackingId={AIRSalesDemoTrackingIds.salesDemoPanelCloseButton}
            />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
        <SurfaceHeader.Body>
          {t(
            'This is a simulated phone call with the AI Receptionist. No real phone calls are being placed by your office.'
          )}
        </SurfaceHeader.Body>
      </SurfaceHeader.Root>
      <SettingsPanelContent css={styles.panelContent}>
        {!demoStarted ? (
          <PhoneNumberSetup onStart={handleStartDemo} onClose={onClose} />
        ) : (
          <PhoneNumberConfirmation phoneNumber={phoneNumber} />
        )}

        <VoiceInterfaceView
          state={state}
          isRecording={isRecording}
          audioLevel={audioLevel}
          silenceThreshold={silenceThreshold}
          onMicButtonPress={handleMicButtonPress}
          onStartNewConversation={handleStartNewConversation}
          settingsChangedBanner={settingsBanner}
          disabled={!demoStarted}
          onViewCalendar={handleViewCalendar}
          showScheduleTextConfirmation={showScheduleTextConfirmation}
          onViewAppointmentRequest={onViewAppointmentRequest}
        />
      </SettingsPanelContent>
    </div>
  );
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    height: 100%;
  `,
  panelContent: css`
    height: 100%;
    background: var(--fabric-color-surface-canvas);
    display: flex;
    flex-direction: column;
    gap: var(--fabric-space-gap-sm);
    padding: var(--fabric-space-padding-sm);
  `,
};
